// Dashboard Ana Sayfa İçeriği
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { 
  Users, Activity, CheckCircle, Zap, UserPlus, Trophy, 
  ChevronRight, RefreshCcw, PieChart 
} from 'lucide-react';
import { cn } from '../../../lib/utils';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Line, PieChart as RechartsPieChart, Pie, Cell 
} from 'recharts';

const DashboardContent = ({ 
  user, 
  students = [], 
  syncLeaderboardData, 
  setActiveSection, 
  openStudentModal,
  weeklyActivityData = [],
  levelDistributionData = []
}) => {
  return (
    <div className="space-y-6">
      {/* Hoşgeldin Kartı */}
      <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-1">Hoş Geldiniz, {user?.name?.split(' ')[0] || 'Admin'}!</h2>
            <p className="text-blue-100">Cabin Crew Training Yönetim Paneline hoş geldiniz</p>
          </div>
          <div className="hidden md:flex items-center gap-4">
            <div className="text-center">
              <p className="text-3xl font-bold">{(students || []).length}</p>
              <p className="text-xs text-blue-200">Toplam Öğrenci</p>
            </div>
            <div className="w-px h-12 bg-white/20" />
            <div className="text-center">
              <p className="text-3xl font-bold">16</p>
              <p className="text-xs text-blue-200">Aktif Modül</p>
            </div>
          </div>
        </div>
      </div>

      {/* İstatistik Kartları */}
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700">📊 Genel İstatistikler</h3>
        <Button 
          onClick={syncLeaderboardData} 
          variant="outline" 
          size="sm"
          className="text-blue-600 border-blue-300 hover:bg-blue-50"
        >
          <RefreshCcw className="w-4 h-4 mr-2" />
          Liderlik Verilerini Senkronize Et
        </Button>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Toplam Öğrenci</p>
                <p className="text-3xl font-bold text-blue-700">{(students || []).length}</p>
                <p className="text-xs text-blue-500 mt-1">+12% bu ay</p>
              </div>
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                <Users className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Aktif Öğrenci</p>
                <p className="text-3xl font-bold text-green-700">{Math.floor((students || []).length * 0.6)}</p>
                <p className="text-xs text-green-500 mt-1">Son 7 gün</p>
              </div>
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                <Activity className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Tamamlanan Ders</p>
                <p className="text-3xl font-bold text-purple-700">1,248</p>
                <p className="text-xs text-purple-500 mt-1">Bu hafta</p>
              </div>
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-50 to-orange-100">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Toplam Mil</p>
                <p className="text-3xl font-bold text-orange-700">45.2K</p>
                <p className="text-xs text-orange-500 mt-1">Kazanılan</p>
              </div>
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Grafikler */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Haftalık Aktivite */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5 text-blue-500" />
              Haftalık Aktivite
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={weeklyActivityData}>
                <defs>
                  <linearGradient id="colorOgrenci" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip 
                  contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                />
                <Area type="monotone" dataKey="ogrenci" stroke="#3b82f6" fillOpacity={1} fill="url(#colorOgrenci)" name="Aktif Öğrenci" />
                <Line type="monotone" dataKey="ders" stroke="#8b5cf6" strokeWidth={2} name="Tamamlanan Ders" />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Seviye Dağılımı */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <PieChart className="w-5 h-5 text-purple-500" />
              Seviye Dağılımı
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center">
              <ResponsiveContainer width="100%" height={250}>
                <RechartsPieChart>
                  <Pie
                    data={levelDistributionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, value }) => `${name}: ${value}%`}
                  >
                    {(levelDistributionData || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex flex-wrap justify-center gap-3 mt-4">
              {(levelDistributionData || []).map(item => (
                <div key={item.name} className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="text-xs text-gray-600">{item.name}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alt Bölüm */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Son Kayıtlar */}
        <Card className="border-0 shadow-lg lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-green-500" />
                Son Kayıt Olan Öğrenciler
              </div>
              <Button variant="ghost" size="sm" onClick={() => setActiveSection('students')}>
                Tümü
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(students || []).slice(0, 5).map((student, idx) => (
                <div 
                  key={student.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-xl hover:bg-gray-100 cursor-pointer transition-colors"
                  onClick={() => openStudentModal(student)}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm",
                      ['bg-blue-100 text-blue-600', 'bg-green-100 text-green-600', 'bg-purple-100 text-purple-600', 'bg-orange-100 text-orange-600', 'bg-pink-100 text-pink-600'][idx % 5]
                    )}>
                      {student.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <p className="font-medium text-gray-800 text-sm">{student.name}</p>
                      <p className="text-xs text-gray-500">{student.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-gray-500">
                      {student.created_at && student.created_at !== '-' 
                        ? new Date(student.created_at).toLocaleDateString('tr-TR')
                        : '-'}
                    </span>
                    <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-lg font-medium">
                      {student.current_level || 'A0'}
                    </span>
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Haftalık Liderler */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Haftalık Liderler
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {[
                { name: 'Elif Y.', miles: 2840, rank: 1 },
                { name: 'Ahmet K.', miles: 2456, rank: 2 },
                { name: 'Zeynep M.', miles: 2198, rank: 3 },
                { name: 'Can B.', miles: 1890, rank: 4 },
                { name: 'Selin T.', miles: 1654, rank: 5 },
              ].map((leader, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2">
                  <div className={cn(
                    "w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm",
                    idx === 0 ? "bg-amber-400 text-white" :
                    idx === 1 ? "bg-gray-300 text-gray-700" :
                    idx === 2 ? "bg-orange-300 text-orange-800" :
                    "bg-gray-100 text-gray-600"
                  )}>
                    {leader.rank}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-800 text-sm">{leader.name}</p>
                    <p className="text-xs text-gray-500">{leader.miles.toLocaleString()} Mil</p>
                  </div>
                  {idx === 0 && <span className="text-lg">🏆</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardContent;
